/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from '@angular/common';
import {HttpEventType} from '@angular/common/http';
import {Component, OnInit, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatTabGroup} from '@angular/material/tabs';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {BUSINESS_PLAN, DISMISS_MSG, PROPULSION_COLUMN_HEADERS, TVP} from '../../../common/constants';
import {SheetSelectorComponent} from '../../../core/components/sheet-selector/sheet-selector.component';
import {VIOLATION_TABS} from '../../../core/enums/propulsion-violation.enum';
import {AutocompleteFilterComponent} from '../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {ColumnHeader} from '../../../core/models/column-header.model';
import {WorksheetExportRequest} from '../../../core/models/worksheet-export-request.model';
import {StateService} from '../../../core/state/state.service';
import {PropulsionPortfolioLoad} from '../../../models/propulsion-portfolio-load.model';
import {PropulsionSnapshotViolation} from '../../../models/propulsion-snapshot-violation.model';
import {PropulsionSnapshot} from '../../../models/propulsion-snapshot.model';
import {UnmatchedVehPropReport} from '../../../models/reports/unmatched-veh-prop-report.model';
import {SelectableOptions} from '../../../models/selectable-options';
import {User} from '../../../models/user.model';
import {VehicleSnapshot} from '../../../models/vehicle-snapshot.model';
import {ExcelExportService} from '../../../services/excel-export.service';
import {PropulsionSnapshotService} from '../../../services/propulsion-snapshot.service';
import {VehicleSnapshotService} from '../../../services/vehicle-snapshot.service';

@Component({
    selector: 'app-propulsion-snapshot',
    templateUrl: './propulsion-snapshot.component.html',
    styleUrls: ['./propulsion-snapshot.component.scss'],
    standalone: false
})
export class PropulsionSnapshotComponent implements OnInit {

  @ViewChild('violationTabGroup') violationTabGroup: MatTabGroup;

  user: User;

  showSpinner: boolean;
  showTable: boolean;
  hasViolations: boolean;

  pageSize: number;
  violationTabIndex: number;

  // values for radio options
  selectedPortfolioType: string;
  businessPlan = BUSINESS_PLAN;
  tvp = TVP;

  displayedData: PropulsionSnapshot[];
  propSnapshotData: Map<string, PropulsionSnapshot[]>;
  processedSnapshot: Map<string, PropulsionPortfolioLoad[]>;
  violations: PropulsionSnapshotViolation;

  unmatchedReportExportRequest: WorksheetExportRequest;
  tableDataExportRequest: WorksheetExportRequest;
  processedSnapshotExportRequest: WorksheetExportRequest;

  file: File;
  sheets: SelectableOptions = new SelectableOptions();

  vehSnapshotColumns: ColumnHeader[] = [
    {name: 'compositeKey.iteration', label: 'ITERATION'},
    {name: 'compositeKey.sevenKeyFields.brand', label: 'BRAND'},
    {name: 'region', label: 'REGION'},
    {name: 'subRegion', label: 'SUBREGION'},
    {name: 'compositeKey.sevenKeyFields.plngMkt', label: 'PLNGMKT'},
    {name: 'cntryRpo', label: 'CNTRYRPO'},
    {name: 'compositeKey.sevenKeyFields.mktDiv', label: 'MKTDIV'},
    {name: 'compositeKey.sevenKeyFields.lbrand', label: 'LBRAND'},
    {name: 'compositeKey.sevenKeyFields.bodyStyle', label: 'BODYSTYLE'},
    {name: 'compositeKey.sevenKeyFields.styleCode', label: 'STYLECODE'},
    {name: 'compositeKey.drvPos', label: 'DRVPOS'},
    {name: 'arch', label: 'ARCH'},
    {name: 'compositeKey.sevenKeyFields.prgmCode', label: 'PRGMCODE'},
    {name: 'engCtr', label: 'ENGCTR'},
    {name: 'compositeKey.gfsVehicleDates.firstMy', label: 'FIRSTMY'},
    {name: 'compositeKey.gfsVehicleDates.lastMy', label: 'LASTMY'},
    {name: 'compositeKey.gfsVehicleDates.sorp', label: 'SORP'},
    {name: 'compositeKey.gfsVehicleDates.eop', label: 'EOP'},
    {name: 'compositeKey.gfsVehicleDates.sos', label: 'SOS'},
    {name: 'compositeKey.gfsVehicleDates.eos', label: 'EOS'},
    {name: 'vehType', label: 'VEHTYPE'},
    {name: 'gseg', label: 'GSEG'}
  ];

  propulsionColumnHeaders: ColumnHeader[] = PROPULSION_COLUMN_HEADERS;

  propSnapshotColDef: ColDef[] = [{
    field: 'iteration',
    headerName: 'Iteration Number',
    filter: AutocompleteFilterComponent,
    lockPosition: true
  },
    {field: 'region', headerName: 'Region of Sale', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'subRegion', headerName: 'Sub-Region', filter: AutocompleteFilterComponent, lockPosition: true},
    {
      field: 'sevenKeyFields.plngMkt',
      headerName: 'Market Operation',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {field: 'cntryRpo', headerName: 'Country RPO', filter: AutocompleteFilterComponent, lockPosition: true},
    {
      field: 'sevenKeyFields.mktDiv',
      headerName: 'Marketing Division',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {field: 'sevenKeyFields.brand', headerName: 'Brand', filter: AutocompleteFilterComponent, lockPosition: true},
    {
      field: 'sevenKeyFields.lbrand',
      headerName: 'Local Brand',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {
      field: 'sevenKeyFields.bodyStyle',
      headerName: 'Body Style',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {
      field: 'sevenKeyFields.styleCode',
      headerName: 'Bodystyle Code',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {field: 'drvPos', headerName: 'Driver Position', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'arch', headerName: 'Architecture', filter: AutocompleteFilterComponent, lockPosition: true},
    {
      field: 'sevenKeyFields.prgmCode',
      headerName: 'Program Code',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {field: 'engCtr', headerName: 'VEC', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'gfsVehicleDates.firstMy', headerName: 'First MY', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'gfsVehicleDates.lastMy', headerName: 'Last MY', filter: AutocompleteFilterComponent, lockPosition: true},
    {
      field: 'gfsVehicleDates.sorp',
      headerName: 'Vehicle SORP',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {field: 'gfsVehicleDates.eop', headerName: 'Vehicle EOP', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'gfsVehicleDates.sos', headerName: 'Vehicle SOS', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'gfsVehicleDates.eos', headerName: 'Vehicle EOS', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'vehType', headerName: 'Vehicle Type', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'gseg', headerName: 'Global Segment', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'engFam', headerName: 'Engine Family', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'engCam', headerName: 'Engine CAM', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'engDsp', headerName: 'Engine Displacement', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'engPwr', headerName: 'Engine Power (Horsepower)', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'engTorq', headerName: 'Engine Torque', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'engRpo', headerName: 'Engine RPO', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'tranFam', headerName: 'Transmission Family', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'tranRpo', headerName: 'Transmission RPO', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'tranType', headerName: 'Transmission Type', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'tranSpeed', headerName: 'Transmission Speed', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'propSorp', headerName: 'Propulsion SORP', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'propEop', headerName: 'Propulsion EOP', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'propFirstMy', headerName: 'Propulsion First MY', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'propLastMy', headerName: 'Propulsion Last MY', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'boost', headerName: 'Boost', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'injection', headerName: 'Injection', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'engCyl', headerName: 'Number of Cylinders', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'vfuel', headerName: 'Vehicle Fuel Type', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'driveType', headerName: 'Drive Type', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'tranName', headerName: 'Transmission Name', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'elecRpo', headerName: 'Electrification RPO', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'elecFam', headerName: 'Electrification Family', filter: AutocompleteFilterComponent, lockPosition: true},
    {
      field: 'elecDesc',
      headerName: 'Electrification RPO Description',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {field: 'batRpo', headerName: 'Battery RPO', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'cellFam', headerName: 'Cell Family', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'batDesc', headerName: 'Battery RPO Description', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'priDrvRpo', headerName: 'Primary Drive Unit RPO', filter: AutocompleteFilterComponent, lockPosition: true},
    {
      field: 'priDrvDesc',
      headerName: 'Primary Drive Unit RPO Description',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {
      field: 'secDrvRpo',
      headerName: 'Secondary Drive Unit RPO',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {
      field: 'secDrvDesc',
      headerName: 'Secondary Drive Unit RPO Description',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    }
  ];

  propSnapshotColumns: ColumnHeader[] = this.propSnapshotColDef.map(col => {
    return {name: col.field, label: col.headerName}
  });

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };

  constructor(public snackBar: FabMessageService,
              private readonly propSnapshotService: PropulsionSnapshotService,
              private readonly vehSnapshotService: VehicleSnapshotService,
              private readonly userService: StateService,
              public readonly excelService: ExcelExportService,
              private readonly dialog: MatDialog) {
  }

  ngOnInit(): void {
    this.user = this.userService.user;
    this.selectedPortfolioType = this.businessPlan;
    this.pageSize = 100;
    this.showSpinner = false;
    this.showTable = false;
  }

  /**
   * Gets the existing Propulsion Snapshot data
   */
  getPropulsionSnapshot(): void {
    this.showSpinner = true;
    this.hasViolations = false;
    this.showTable = false;
    this.propSnapshotService.getPropulsionSnapshot().subscribe(data => {
      this.displayedData = data;
    }, err => {
      this.snackBar.displayError(err, DISMISS_MSG);
      this.showSpinner = false;
    }, () => {
      this.showSpinner = false;
      this.showTable = true;
      this.setTableDataExportRequest();
    });
  }

  /**
   * Get the Unmatched Vehicle snapshot and propulsion snapshot data. Then sets the export request to export the data
   * as a report.
   */
  getUnmatchedVehAndPropReport(): void {
    this.showSpinner = true;
    const unmatchedVehPropReport = new UnmatchedVehPropReport();
    this.propSnapshotService.getUnmatchedPropulsionSnapshot().subscribe((data: PropulsionSnapshot[]) => {
      unmatchedVehPropReport.unmatchedPropSnapshot = data;
    }, err => {
      this.showSpinner = false;
      this.snackBar.displayError(err, DISMISS_MSG);
    }, () => {
      this.vehSnapshotService.getUnmatchedVehicleSnapshot().subscribe((data: VehicleSnapshot[]) => {
        unmatchedVehPropReport.unmatchedVehSnapshot = data;
      }, err => {
        this.showSpinner = false;
        this.snackBar.displayError(err, DISMISS_MSG);
      }, () => {
        this.showSpinner = false;
        this.exportUnmatchedVehPropReport(unmatchedVehPropReport);
      });
    });
  }

  /**
   * Loads the GM3PD Propulsion Snapshot file. Validation on the data is performed before the load happens.
   * If no violations found during validation, then the data will be loaded.
   */
  loadGM3PDPropSnapshotData(): void {
    this.showSpinner = true;
    this.hasViolations = false;
    this.propSnapshotService.transform(this.file, this.sheets.selectedOptions).subscribe(e => {
      if (e.type === HttpEventType.Response) {
        this.propSnapshotData = e.body;
      }
    }, err => {
      this.snackBar.displayError(err, DISMISS_MSG);
      this.showSpinner = false;
    }, () => {
      this.validate();
    });
  }

  /**
   * Validate the GM3PD Snapshot data
   */
  validate(): void {
    this.showSpinner = true;
    this.propSnapshotService.validate(this.propSnapshotData).subscribe(data => {
      this.violations = data[this.sheets.selectedOptions[0]];
      if (this.violations && !this.violations.vFuelViolations) {
        this.violations.vFuelViolations = [];
      }
    }, err => {
      this.snackBar.displayError(err, DISMISS_MSG);
      this.showSpinner = false;
    }, () => {
      const firstNonEmptyValidationList: string = this.getFirstNonEmptyValidationList();
      this.hasViolations = this.violations !== undefined && firstNonEmptyValidationList !== undefined;
      if (this.hasViolations) {
        this.snackBar.displayError('There were violations found in the propulsion snapshot', DISMISS_MSG);
        this.displayedData = this.violations[firstNonEmptyValidationList];
        this.showTable = true;
        this.setTableDataExportRequest();
        this.showSpinner = false;
      } else {
        // If there are no violations, load the data
        this.load();
      }
    });
  }

  /**
   * Load the propulsion snapshot data
   */
  load(): void {
    this.showSpinner = true;
    this.propSnapshotService.load(this.propSnapshotData).subscribe(() => {
    }, err => {
      this.snackBar.displayError(err, DISMISS_MSG);
      this.showSpinner = false;
    }, () => {
      this.snackBar.displaySuccess('Propulsion Snapshot was loaded successfully.', DISMISS_MSG);
      this.displayedData = this.propSnapshotData[this.sheets.selectedOptions[0]];
      this.showTable = true;
      this.setTableDataExportRequest();
      this.showSpinner = false;
    });
  }

  /**
   * Process the snapshot that is currently loaded into the system
   */
  processSnapshot(): void {
    this.showSpinner = true;
    this.propSnapshotService.processSnapshot(this.selectedPortfolioType, this.user.gmid).subscribe(data => {
      this.processedSnapshot = data;
    }, err => {
      this.snackBar.displayError(err, DISMISS_MSG);
      this.showSpinner = false;
    }, () => {
      this.exportProcessedSnapshot();
    });
  }

  /**
   * Event handler for when an upload event is emitted.
   * @param event The event that is emitted by the Upload button component
   */
  upload(event: any): void {
    this.sheets.options = event.get('data');
    this.file = event.get('file');
    this.onFileUpload();
  }

  /**
   * Method run after uploading an Excel file.
   * Opens the SheetSelectorComponent, where a user can select which sheets to grab
   * from the uploaded Excel file.
   */
  onFileUpload(): void {
    this.dialog.open(SheetSelectorComponent, {
      data: {
        sheets: this.sheets.options,
        isMultiSelect: false
      },
      disableClose: true,
      width: '250px'
    }).afterClosed().subscribe(
      data => {
        // Clear out the selected options before setting them in case the user already uploaded a file and
        // selected an option previously
        this.sheets.selectedOptions = [];
        if (data) {
          this.sheets.selectedOptions = data;
        }
      },
      error => {
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      () => {
        if (this.sheets.selectedOptions.length > 0) {
          this.loadGM3PDPropSnapshotData();
        }
      }
    );
  }

  /**
   * Exports the Unmatched Vehicle and Propulsion Record report
   * @param unmatchedVehPropReport the unmatchedVehProp report object that contains the data
   */
  exportUnmatchedVehPropReport(unmatchedVehPropReport: UnmatchedVehPropReport): void {
    const excelData = new Map<string, any>();
    excelData['in-prop-not-in-veh'] = unmatchedVehPropReport.unmatchedPropSnapshot;
    excelData['in-veh-not-in-prop'] = unmatchedVehPropReport.unmatchedVehSnapshot;
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    this.unmatchedReportExportRequest = this.excelService.createExportRequestBody('UnmatchedVehPropReport_' + currDate,
      excelData,
      [['in-prop-not-in-veh', this.propSnapshotColumns], ['in-veh-not-in-prop', this.vehSnapshotColumns]]);
    this.excelService.transformToExcel(this.unmatchedReportExportRequest).subscribe(
      (file: any) => {
        this.excelService.exportToExcelFromServer(file, this.unmatchedReportExportRequest.fileName);
      },
      () => {
        this.snackBar.displayError('Error with exporting data.', DISMISS_MSG);
        this.showSpinner = false;
      }, () => {
        this.showSpinner = false;
      });
  }

  /**
   * Sets the export data request for exporting the table data
   */
  setTableDataExportRequest(): void {
    // If the table data is displaying violations, then export those sheets, else just export the table data
    setTimeout(() => {
      if (this.hasViolations) {
        this.setViolationExportRequest();
      } else {
        const currDate = new Date().toISOString();
        const excelData = new Map<string, any>();
        excelData['PROP_Snapshot'] = this.displayedData;

        // Send the request
        this.tableDataExportRequest = this.excelService.createExportRequestBody('CurrentGM3PDPropSnap_' + currDate,
          excelData,
          [['PROP_Snapshot', this.propSnapshotColumns]]);
      }
    });
  }

  /**
   * Export the processed snapshot data as Excel file
   */
  exportProcessedSnapshot(): void {
    const sheetList: [string, ColumnHeader[]][] = [];
    Object.keys(this.processedSnapshot).forEach(key => {
      sheetList.push([key, this.propulsionColumnHeaders]);
    });
    const currDate = new Date().toISOString();
    this.processedSnapshotExportRequest = this.excelService.createExportRequestBody('PropPortForGFSLoad_' + currDate,
      this.processedSnapshot,
      sheetList);
    this.excelService.transformToExcel(this.processedSnapshotExportRequest).subscribe(
      (file: any) => {
        this.excelService.exportToExcelFromServer(file, this.processedSnapshotExportRequest.fileName);
      },
      () => {
        this.snackBar.displayError('Error with exporting data.', DISMISS_MSG);
        this.showSpinner = false;
      }, () => {
        this.showSpinner = false;
      });
  }

  /**
   * Gets the name of the first non-empty validation list
   * @return string - name of the first non-empty validation list, undefined if not found
   */
  getFirstNonEmptyValidationList(): string {
    return Object.keys(this.violations).find(key => this.violations[key].length > 0);
  }

  /**
   * Update the datasource of the Violation table data based off the tab change
   * @param $event The event that is emitted when a tab was changed
   */
  violationTabChange($event): void {
    if ($event !== undefined && $event.index !== -1) {
      switch ($event.tab.textLabel) {
        case VIOLATION_TABS.DUPLICATE_ENG_DTL:
          this.displayedData = this.violations.duplicateEngList;
          break;
        case VIOLATION_TABS.DUPLICATE_TRAN_DTL:
          this.displayedData = this.violations.duplicateTranList;
          break;
        case VIOLATION_TABS.DUPLICATE_BAT:
          this.displayedData = this.violations.duplicateBatList;
          break;
        case VIOLATION_TABS.DUPLICATE_ELEC:
          this.displayedData = this.violations.duplicateElecList;
          break;
        case VIOLATION_TABS.DUPLICATE_PRI_DRV:
          this.displayedData = this.violations.duplicatePriDrvList;
          break;
        case VIOLATION_TABS.DUPLICATE_SEC_DRV:
          this.displayedData = this.violations.duplicateSecDrvList;
          break;
        case VIOLATION_TABS.DATE_VIOLATION:
          this.displayedData = this.violations.dateViolations;
          break;
        case VIOLATION_TABS.ITERATION_VIOLATION:
          this.displayedData = this.violations.iterationViolations;
          break;
        case VIOLATION_TABS.VFUEL_VIOLATION:
          this.displayedData = this.violations.vFuelViolations;
          break;
        default:
          break;
      }
    }
  }

  /**
   * Helper function to create the Export request for the violation sheets
   */
  setViolationExportRequest(): void {
    if (!this.violationTabGroup) {
      return;
    }
    const currDate = new Date().toISOString();
    const columnHeaderMap = new Map<string, ColumnHeader[]>();
    const fcorg = 'fcOrg';
    // Set the columns used for each violation sheet
    columnHeaderMap[VIOLATION_TABS.DUPLICATE_ENG_DTL] = this.propSnapshotColumns.filter(
      header => [fcorg, 'engRpo', 'engCyl', 'injection', 'boost'].includes(header.name));
    columnHeaderMap[VIOLATION_TABS.DUPLICATE_TRAN_DTL] = this.propSnapshotColumns.filter(
      header => [fcorg, 'tranRpo', 'tranName', 'tranSpeed'].includes(header.name));
    // Special edge case for the Bat Violation sheet (business wants the batRpo col to display before cellFam, batDesc)
    columnHeaderMap[VIOLATION_TABS.DUPLICATE_BAT] = this.propSnapshotColumns.filter(
      header => [fcorg, 'batRpo'].includes(header.name));
    columnHeaderMap[VIOLATION_TABS.DUPLICATE_BAT] = [...columnHeaderMap[VIOLATION_TABS.DUPLICATE_BAT],
      ...this.propSnapshotColumns.filter(header => ['cellFam', 'batDesc'].includes(header.name))];
    columnHeaderMap[VIOLATION_TABS.DUPLICATE_ELEC] = this.propSnapshotColumns.filter(
      header => [fcorg, 'elecRpo', 'elecFam', 'elecDesc'].includes(header.name));
    columnHeaderMap[VIOLATION_TABS.DUPLICATE_PRI_DRV] = this.propSnapshotColumns.filter(
      header => [fcorg, 'priDrvRpo', 'priDrvDesc'].includes(header.name));
    columnHeaderMap[VIOLATION_TABS.DUPLICATE_SEC_DRV] = this.propSnapshotColumns.filter(
      header => [fcorg, 'secDrvRpo', 'secDrvDesc'].includes(header.name));
    columnHeaderMap[VIOLATION_TABS.DATE_VIOLATION] = this.propSnapshotColumns;
    columnHeaderMap[VIOLATION_TABS.ITERATION_VIOLATION] = this.propSnapshotColumns;
    columnHeaderMap[VIOLATION_TABS.VFUEL_VIOLATION] = this.propSnapshotColumns;
    const violationTabs: [string, ColumnHeader[]][] = this.violationTabGroup._allTabs.toArray().map(tab => {
      return [tab.textLabel, columnHeaderMap[tab.textLabel]];
    });
    const data = new Map<string, any>();
    data[VIOLATION_TABS.DUPLICATE_ENG_DTL] = this.violations.duplicateEngList;
    data[VIOLATION_TABS.DUPLICATE_TRAN_DTL] = this.violations.duplicateTranList;
    data[VIOLATION_TABS.DUPLICATE_BAT] = this.violations.duplicateBatList;
    data[VIOLATION_TABS.DUPLICATE_ELEC] = this.violations.duplicateElecList;
    data[VIOLATION_TABS.DUPLICATE_PRI_DRV] = this.violations.duplicatePriDrvList;
    data[VIOLATION_TABS.DUPLICATE_SEC_DRV] = this.violations.duplicateSecDrvList;
    data[VIOLATION_TABS.DATE_VIOLATION] = this.violations.dateViolations;
    data[VIOLATION_TABS.ITERATION_VIOLATION] = this.violations.iterationViolations;
    data[VIOLATION_TABS.VFUEL_VIOLATION] = this.violations.vFuelViolations;
    // Send the request
    this.tableDataExportRequest = this.excelService.createExportRequestBody('GM3PDPropSnapViolations_' + currDate,
      data,
      violationTabs);
  }

}
